/* -------------------------------------------------------------------------
   Alt-text gallery filtering v2.1 (ECOM-30 A, 2026-09-02; opus review fixes)
   Shows only the media that belong to the selected colour. Alt-text conventions
   (set by the Photo Manager):
     "<Colour value>"               product photo of that colour  (e.g. "Tortoise / Dark Brown")
     "Polarized <Colour>", "Non-Polarized - <Colour>"  same, prefix ignored (alt side only)
     "lifestyle"                    shared lifestyle shot, shown for every colour
     "lifestyle | <Colour value>"   lifestyle shot of ONE colour, shown for that colour only
     "" / anything unrecognised     shown for every colour (never hide what we cannot classify)
   FAIL-OPEN RULE: if the selected colour has no media of its own, nothing is hidden —
   the customer sees the full gallery, never an empty one. If two colour values of a product
   normalise to the same key, that product is never filtered.
   Scope: a variant:change only touches the gallery bound to the form that dispatched it.
   Runs (1) once the gallery custom elements exist (3 s timeout, fail-open), (2) on every
   variant:change (bubble) plus a microtask re-assert after the theme's own scroll logic,
   (3) synchronously on every childList mutation inside the section (covers re-renders,
   including combined-listing product changes), (4) on section load. After filtering it
   re-selects the variant's featured media by data-media-id so the visible slide matches the swatch.
   One shared implementation per page; every section registers its own observer.
   -------------------------------------------------------------------------*/
(function () {
  'use strict';
  if (window.__altFilter) return;

  /* ECOM-59: the section ids used to be baked in by Liquid, one full copy of
     this script per product-gallery render. Now each render pushes only its own
     id onto this queue (see snippets/product-gallery.liquid) and this single
     cached copy observes every registered section. The stub also calls
     observeSection directly if we happen to be loaded already, so ordering
     between the deferred asset and the inline stubs does not matter. */
  var REGISTERED = (window.__altFilterSections = window.__altFilterSections || []);

  var DASHES = /[\-‐‑‒–—−:|]+/g;
  function normColor(s, isAlt) {
    s = (s || '');
    try { s = s.normalize('NFC'); } catch (_) {}
    s = s.toLowerCase();
    if (isAlt) s = s.replace(/\bnon[\s\-‐‑]*polari[sz]ed\b|\bpolari[sz]ed\b/g, '');
    s = s.replace(DASHES, ' ');
    s = s.replace(/\s*\/\s*/g, ' / ');
    s = s.replace(/\s+/g, ' ').replace(/^[\s\/]+|[\s\/]+$/g, '');
    return s;
  }
  // -> { kind: 'shared' } | { kind: 'color', color: '<normalised>' }
  function classifyAlt(alt) {
    var raw = (alt || '').trim(), low = raw.toLowerCase();
    if (!raw || low === 'lifestyle') return { kind: 'shared' };
    if (low.indexOf('lifestyle') === 0) {
      var rest = raw.slice(9).replace(/^[\s\-‐‑‒–—:|]+/, '');
      return rest ? { kind: 'color', color: normColor(rest, true) } : { kind: 'shared' };
    }
    return { kind: 'color', color: normColor(raw, true) };
  }
  function readJson(el) { try { return JSON.parse((el && el.textContent) || 'null'); } catch (_) { return null; } }
  function pickerFor(gallery) {
    var formId = gallery.getAttribute('form');
    return formId ? document.querySelector('variant-picker[form-id="' + CSS.escape(formId) + '"]') : null;
  }
  function colorPositions(gallery) {
    return (gallery.dataset.colorPositions || '').split(',').map(Number).filter(Boolean);
  }
  // All colour values this product offers (normalised, option side). Empty object = never filter.
  function productColors(gallery) {
    var picker = pickerFor(gallery), positions = colorPositions(gallery), out = Object.create(null);
    var opts = picker ? readJson(picker.querySelector('script[data-product-options]')) : null;
    if (!opts || !positions.length) return out;
    var lists = positions.map(function (p) {
      var o = opts.filter(function (x) { return Number(x.position) === p; })[0];
      return (o && o.values) ? o.values.map(function (v) { return v.name; }) : [];
    });
    var combos = lists.reduce(function (acc, list) {
      var next = [];
      acc.forEach(function (a) { list.forEach(function (b) { next.push(a ? a + ' / ' + b : b); }); });
      return next;
    }, ['']);
    var seen = Object.create(null), collision = false;
    combos.forEach(function (c) {
      if (!c) return;
      var k = normColor(c, false);
      if (seen[k] && seen[k] !== c) collision = true;
      seen[k] = c; out[k] = true;
    });
    return collision ? Object.create(null) : out;
  }
  function colorFromVariant(variant, positions) {
    if (!variant || !positions.length) return null;
    var parts = positions.map(function (p) { return (variant['option' + p] || '').trim(); }).filter(Boolean);
    return parts.length ? parts.join(' / ') : null;
  }
  function currentVariant(gallery) {
    var picker = pickerFor(gallery);
    return picker ? readJson(picker.querySelector('script[data-variant]')) : null;
  }

  function applyFilter(gallery, variant) {
    var carousel = gallery.querySelector('.product-gallery__carousel');
    if (!carousel || typeof carousel.filter !== 'function') return false;
    // index space = carousel.allCells = direct children; non-media children are never hidden
    var cells = Array.prototype.slice.call(carousel.children);
    if (!cells.length) return false;
    var positions = colorPositions(gallery);
    var selected = normColor(colorFromVariant(variant, positions), false);
    var known = productColors(gallery);
    var classes = cells.map(function (m) {
      if (!m.classList || !m.classList.contains('product-gallery__media')) return { kind: 'shared' };
      var el = m.querySelector('img, video, iframe');
      return classifyAlt(el ? el.getAttribute('alt') : '');
    });
    var hidden = []; // 1-based positions
    if (selected) {
      var hasOwn = classes.some(function (c) { return c.kind === 'color' && c.color === selected; });
      if (hasOwn) {
        classes.forEach(function (c, i) {
          // hide only media that provably belong to ANOTHER colour of this product
          if (c.kind === 'color' && c.color !== selected && known[c.color] === true) hidden.push(i + 1);
        });
      }
    }
    gallery.setAttribute('filtered-indexes', JSON.stringify(hidden));
    carousel.filter(hidden.map(function (p) { return p - 1; }));
    gallery.querySelectorAll('.product-gallery__thumbnail[data-media-position]').forEach(function (btn) {
      btn.toggleAttribute('hidden', hidden.indexOf(parseInt(btn.getAttribute('data-media-position'), 10)) !== -1);
    });

    // Land on the variant's own featured media (falls back to the first visible cell).
    var visible = cells.filter(function (c, i) { return hidden.indexOf(i + 1) === -1; });
    var featId = variant && variant.featured_media ? String(variant.featured_media.id) : null;
    var target = null;
    if (featId) target = visible.filter(function (c) { return c.getAttribute('data-media-id') === featId; })[0] || null;
    if (!target) target = visible[0] || null;
    if (target) {
      try {
        if (carousel.isScrollable && typeof carousel.select === 'function') {
          carousel.select(visible.indexOf(target), { instant: true });
        } else {
          target.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'instant' });
        }
      } catch (_) {}
    }
    return true;
  }

  // formId (optional) scopes the update to the gallery bound to that product form
  function applyAll(variantOverride, formId) {
    document.querySelectorAll('product-gallery[data-color-positions]').forEach(function (gallery) {
      if (formId && gallery.getAttribute('form') !== formId) return;
      try {
        var v = variantOverride || currentVariant(gallery);
        if (v) applyFilter(gallery, v);
      } catch (_) {}
    });
  }

  // (2) variant change: scoped to the dispatching form; re-assert after the theme's own handler
  document.addEventListener('variant:change', function (e) {
    if (!e.detail || !e.detail.variant) return;
    var formId = e.target && e.target.getAttribute ? e.target.getAttribute('id') : null;
    var variant = e.detail.variant;
    applyAll(variant, formId);
    Promise.resolve().then(function () { applyAll(variant, formId); });
  });

  // (3) any structural change inside a section (re-renders, product swaps): re-filter at once.
  // childList only, so our own attribute toggles never re-trigger it.
  var observed = Object.create(null);
  function observeSection(sectionId) {
    if (observed[sectionId]) return;
    var sectionEl = document.getElementById(sectionId);
    if (!sectionEl) return;
    observed[sectionId] = true;
    new MutationObserver(function (muts) {
      if (!muts.some(function (m) { return m.type === 'childList'; })) return;
      applyAll();
    }).observe(sectionEl, { childList: true, subtree: true });
    applyAll();
  }
  window.__altFilter = { applyAll: applyAll, observeSection: observeSection };

  // (1) wait for the theme's custom elements (deferred module) — capped so a broken theme.js
  // degrades to the unfiltered gallery instead of never running.
  function whenReady(fn) {
    var g = document.querySelector('product-gallery'), c = g && g.querySelector('.product-gallery__carousel');
    var names = ['product-gallery'].concat(c ? [c.tagName.toLowerCase()] : []);
    var defs = Promise.all(names.filter(function (n) { return n.indexOf('-') !== -1; }).map(function (n) { return customElements.whenDefined(n); }));
    Promise.race([defs, new Promise(function (r) { setTimeout(r, 3000); })]).then(fn, fn);
  }
  function observeRegistered() { REGISTERED.slice().forEach(observeSection); }
  function init() { observeRegistered(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { whenReady(init); });
  else whenReady(init);
  document.addEventListener('shopify:section:load', function (e) {
    setTimeout(function () {
      if (e.target && e.target.id) observeSection(e.target.id);
      else observeRegistered();
      applyAll();
    }, 100);
  });
})();
